import { mat4, quat, ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { resampleUint8Grid } from '~/domain/services/resampleGrid';
import { Grid } from '~/engine/data/common/Grid';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { View } from '~/engine/view/View';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { ToolAction } from '../../ToolAction';
import {
    buildPatientSrcFromDstAxisScale,
    buildPatientSrcFromDstScale,
    buildSnappedGrid,
    computeMaxAxisScale,
    computeMaxScale,
    flattenSlices,
    unflattenToSlices,
} from './transformUtils';

/**
 * Describes how the scale action operates:
 *
 * - `uniform`: all three axes scale by the same factor
 *   (triggered by corner handles).
 * - `single-axis`: only one local grid axis scales
 *   (triggered by edge midpoint handles).
 */
export type ScaleMode =
    | { type: 'uniform' }
    | {
          type: 'single-axis';
          /** Patient-space direction of the scale axis. */
          axis: ReadonlyVec3;
          /** Which 2D canvas axis the user drags along. */
          canvasAxis: 'x' | 'y';
      };

/**
 * Action that handles interactive scaling of a structure's SDF
 * texture around the structure's geometric centre.
 *
 * Supports both **uniform** (all axes) and **single-axis** scaling.
 *
 * During drag only the grid transform is updated (resolution is
 * scaled so the same pixel data covers a larger or smaller physical
 * extent). On release the SDF pixels are resampled once into a
 * properly-sized grid with the original resolution, snapped to the
 * image grid's voxel lattice.
 */
export class StructureScaleAction extends ToolAction {
    private readonly _roi: RegionOfInterest;
    private readonly _mode: ScaleMode;

    /** The view in which the drag started — all coordinate
     *  conversions must use this view to avoid cross-view
     *  projection mismatches. */
    private _startView?: PlaneView;
    private _centreCanvas?: ReadonlyVec2;
    private _structureCentre?: ReadonlyVec3;
    private _startDist2D?: number;
    private _originalGrid?: Grid;
    private _originalFlatPixels?: Uint8Array;
    private _textureWrite?: TextureWrite;
    private _lastAppliedScale = 1.0;

    /**
     * For single-axis mode: which local grid axis (0=X, 1=Y, 2=Z)
     * the patient-space axis maps to. Computed on mouse press.
     */
    private _localAxisIndex = 0;

    constructor(roi: RegionOfInterest, mode: ScaleMode) {
        super();
        this._roi = roi;
        this._mode = mode;
    }

    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: View
    ): EventHandlerResult {
        if (!(view instanceof PlaneView)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const texture = this._roi.texture;
        if (!texture || texture.isEmpty) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        this._startView = view;
        this._structureCentre = texture.textureGrid.center;
        this._centreCanvas = view.canvasCoordsFromPatientCoords(
            this._structureCentre
        );

        const canvasCoords = view.clientToCanvas(coordinates);

        if (this._mode.type === 'uniform') {
            this._startDist2D = vec2.distance(
                canvasCoords as vec2,
                this._centreCanvas as vec2
            );
        } else {
            // Single-axis: measure distance along the relevant
            // canvas axis only
            const idx = this._mode.canvasAxis === 'x' ? 0 : 1;
            this._startDist2D = Math.abs(
                canvasCoords[idx] - this._centreCanvas[idx]
            );

            // Determine which local grid axis the patient-space
            // axis maps to by projecting into the grid's local frame
            // and finding the dominant component.
            const localVec = texture.textureGrid.rotateToGrid(
                this._mode.axis as vec3
            );
            this._localAxisIndex = dominantAxisIndex(localVec);
        }

        if (this._startDist2D < 1) {
            this._startDist2D = 1;
        }

        this._originalGrid = texture.textureGrid.clone();
        this._originalFlatPixels = flattenSlices(
            texture.pixels,
            this._originalGrid
        );
        this._lastAppliedScale = 1.0;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        if (
            !this._startView ||
            !this._centreCanvas ||
            !this._structureCentre ||
            !this._startDist2D ||
            !this._originalGrid ||
            !this._originalFlatPixels
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const canvasCoords = this._startView.clientToCanvas(coordinates);
        const scaleFactor = this.computeScaleFactor(canvasCoords);
        const clampedScale = Math.max(0.2, Math.min(5.0, scaleFactor));

        if (Math.abs(clampedScale - this._lastAppliedScale) < 0.01) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true
            );
        }

        if (!this._textureWrite) {
            this._textureWrite = this._roi.startModify('scale');
        }

        const texture = this._roi.texture;
        if (!texture) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        const imageGrid = texture.grid;
        const maxScale =
            this._mode.type === 'uniform'
                ? computeMaxScale(this._originalGrid, imageGrid)
                : computeMaxAxisScale(
                      this._originalGrid,
                      imageGrid,
                      this._localAxisIndex
                  );
        const boundedScale = Math.min(clampedScale, maxScale);

        const scaleVec = this.buildScaleVec(boundedScale);
        const scaledGrid = this.buildScaledGrid(scaleVec);
        texture.resetGrid(scaledGrid);
        this._lastAppliedScale = boundedScale;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(
                undefined,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2
    ): EventHandlerResult {
        if (this._textureWrite) {
            try {
                const texture = this._roi.texture;
                if (
                    texture &&
                    this._originalGrid &&
                    this._originalFlatPixels &&
                    this._structureCentre
                ) {
                    const scaleVec = this.buildScaleVec(this._lastAppliedScale);
                    const dstGrid = this.buildResampledGrid(scaleVec);
                    const snappedGrid = buildSnappedGrid(dstGrid, texture.grid);

                    const patientSrcFromDst =
                        this._mode.type === 'uniform'
                            ? buildPatientSrcFromDstScale(
                                  this._structureCentre,
                                  this._lastAppliedScale
                              )
                            : buildPatientSrcFromDstAxisScale(
                                  this._structureCentre,
                                  scaleVec
                              );

                    const result = resampleUint8Grid(
                        this._originalFlatPixels,
                        this._originalGrid,
                        snappedGrid,
                        { patientSrcFromDst, backgroundValue: 0 }
                    );
                    const snappedPixels = unflattenToSlices(
                        result.pixels,
                        snappedGrid.sizeX,
                        snappedGrid.sizeY,
                        snappedGrid.sizeZ
                    );
                    texture.resetSdfAndGrid(snappedPixels, snappedGrid);
                }
            } finally {
                this._textureWrite.finish();
            }
        }

        this._startView = undefined;
        this._centreCanvas = undefined;
        this._structureCentre = undefined;
        this._startDist2D = undefined;
        this._originalGrid = undefined;
        this._originalFlatPixels = undefined;
        this._textureWrite = undefined;

        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            false,
            new VolumeViewUpdateEvent(
                undefined,
                LayerBit.CONTOUR_LAYER_BIT,
                true
            )
        );
    }

    // ── Scale factor computation ─────────────────────────────

    private computeScaleFactor(canvasCoords: ReadonlyVec2): number {
        if (this._mode.type === 'uniform') {
            const currentDist = vec2.distance(
                canvasCoords as vec2,
                this._centreCanvas! as vec2
            );
            return currentDist / this._startDist2D!;
        }

        // Single-axis: displacement along the relevant canvas axis
        const idx = this._mode.canvasAxis === 'x' ? 0 : 1;
        const currentDist = Math.abs(
            canvasCoords[idx] - this._centreCanvas![idx]
        );
        return currentDist / this._startDist2D!;
    }

    /**
     * Build a vec3 scale factor from the scalar scale. For uniform
     * mode all components are the same; for single-axis mode only
     * the relevant local grid axis is scaled.
     */
    private buildScaleVec(scale: number): vec3 {
        if (this._mode.type === 'uniform') {
            return vec3.fromValues(scale, scale, scale);
        }

        const sv = vec3.fromValues(1, 1, 1);
        sv[this._localAxisIndex] = scale;
        return sv;
    }

    // ── Grid builders ────────────────────────────────────────

    /**
     * Build a grid with the same voxel dimensions as the original
     * but with resolution scaled by {@link scaleVec}, centred on
     * the structure's geometric centre. Used during drag for
     * visual preview without resampling.
     */
    private buildScaledGrid(scaleVec: ReadonlyVec3): Grid {
        const srcGrid = this._originalGrid!;
        const orientation = srcGrid.getOrientation();
        const res = srcGrid.getRes();

        const scaledRes = vec3.fromValues(
            res[0] * scaleVec[0],
            res[1] * scaleVec[1],
            res[2] * scaleVec[2]
        );

        const halfSizeMm = vec3.fromValues(
            (srcGrid.sizeX * scaledRes[0]) / 2,
            (srcGrid.sizeY * scaledRes[1]) / 2,
            (srcGrid.sizeZ * scaledRes[2]) / 2
        );
        const halfSizeWorld = vec3.transformMat3(
            vec3.create(),
            halfSizeMm,
            orientation
        );
        const newOrigin = vec3.subtract(
            vec3.create(),
            this._structureCentre!,
            halfSizeWorld
        );

        const newTransform = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), orientation),
            newOrigin
        );

        return new Grid(
            srcGrid.sizeX,
            srcGrid.sizeY,
            srcGrid.sizeZ,
            scaledRes[0],
            scaledRes[1],
            scaledRes[2],
            newTransform
        );
    }

    /**
     * Build a destination grid with the original resolution but
     * scaled voxel count along each axis, centred on the
     * structure's geometric centre. Used on release for the
     * final resample.
     */
    private buildResampledGrid(scaleVec: ReadonlyVec3): Grid {
        const srcGrid = this._originalGrid!;
        const orientation = srcGrid.getOrientation();
        const res = srcGrid.getRes();

        const newSizeX = Math.max(1, Math.round(srcGrid.sizeX * scaleVec[0]));
        const newSizeY = Math.max(1, Math.round(srcGrid.sizeY * scaleVec[1]));
        const newSizeZ = Math.max(1, Math.round(srcGrid.sizeZ * scaleVec[2]));

        const halfSizeMm = vec3.fromValues(
            (newSizeX * res[0]) / 2,
            (newSizeY * res[1]) / 2,
            (newSizeZ * res[2]) / 2
        );
        const halfSizeWorld = vec3.transformMat3(
            vec3.create(),
            halfSizeMm,
            orientation
        );
        const newOrigin = vec3.subtract(
            vec3.create(),
            this._structureCentre!,
            halfSizeWorld
        );

        const newTransform = mat4.fromRotationTranslation(
            mat4.create(),
            quat.fromMat3(quat.create(), orientation),
            newOrigin
        );

        return new Grid(
            newSizeX,
            newSizeY,
            newSizeZ,
            res[0],
            res[1],
            res[2],
            newTransform
        );
    }
}

/**
 * Return the index of the component with the largest absolute
 * value in a vec3. Used to find the dominant local grid axis
 * that a patient-space direction maps to.
 */
function dominantAxisIndex(v: ReadonlyVec3): number {
    const ax = Math.abs(v[0]);
    const ay = Math.abs(v[1]);
    const az = Math.abs(v[2]);
    if (ax >= ay && ax >= az) {
        return 0;
    }
    if (ay >= az) {
        return 1;
    }
    return 2;
}
